import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function shareContent(title: string, text: string, url: string) {
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
      return true;
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
      return false;
    }
  }
  return false;
}

export function copyToClipboard(text: string): Promise<boolean> {
  return navigator.clipboard.writeText(text)
    .then(() => true)
    .catch((error) => {
      console.error('Failed to copy:', error);
      return false;
    });
}

// Function to extract video URLs from text
export function extractVideoUrl(text: string): string | null {
  // Regular expressions for different video platforms
  const patterns = {
    youtube: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)(?:\S+)?/i,
    vimeo: /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|)(\d+)(?:$|\/|\?)/i,
    tiktok: /(?:https?:\/\/)?(?:(?:www|vm)\.)?tiktok\.com(?:\/[@\w.-]+\/video\/|\/)(\d+)/i
  };

  // Check for each video platform
  for (const [platform, pattern] of Object.entries(patterns)) {
    const match = text.match(pattern);
    if (match) {
      const videoId = match[1];
      switch (platform) {
        case 'youtube':
          return `https://www.youtube-nocookie.com/embed/${videoId}`;
        case 'vimeo':
          return `https://player.vimeo.com/video/${videoId}`;
        case 'tiktok':
          return `https://www.tiktok.com/embed/${videoId}`;
        default:
          return null;
      }
    }
  }

  return null;
}

// Function to get video platform from URL
export function getVideoPlatform(url: string): string | null {
  if (url.includes('youtube.com/embed/') || url.includes('youtube-nocookie.com/embed/')) {
    return 'youtube';
  } else if (url.includes('player.vimeo.com/video/')) {
    return 'vimeo';
  } else if (url.includes('tiktok.com/embed/')) {
    return 'tiktok';
  }
  return null;
}